import React, { useEffect, useRef, useState } from "react";
import {
  StyledPanel,
  StyledPanelHeader,
  StyledRightComponent,
} from "./Panel.styled.";
import { FontWeightEnum, IconSizeEnum } from "../../../types/AppTypes";
import { BodyTyp } from "../../Typography/Typography";
import { IconManager } from "../IconManager/IconManager";
import { ReactComponent as CloseIcon } from "../../../icons/close.svg";

interface PanelProps {
  fullHeight?: boolean;
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  width?: number;
  title: string;
  rightComponent?: React.ReactNode;
}

export const Panel = ({
  fullHeight = false,
  isOpen,
  onClose,
  children,
  width,
  title,
  rightComponent,
}: PanelProps) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const [isAnimatedOpen, setIsAnimatedOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setIsAnimatedOpen(false);
      return;
    }
    const raf = requestAnimationFrame(() => setIsAnimatedOpen(true));
    return () => cancelAnimationFrame(raf);
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!panelRef.current) {
        return;
      }

      const target = event.target as HTMLElement;

      // Overlays in portal (es. PopupConfirm) vivono fuori dal DOM del
      // pannello: un click lì dentro non deve chiudere il pannello.
      const overlay = target.closest?.(".modal-overlay, .modal-box");
      if (overlay && !overlay.contains(panelRef.current)) {
        return;
      }

      if (!panelRef.current.contains(target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  return (
    <StyledPanel
      ref={panelRef}
      $isOpen={isAnimatedOpen}
      $width={width}
      $fullHeight={fullHeight}
    >
      <StyledPanelHeader>
        <BodyTyp variant="1" fontWeight={FontWeightEnum.SEMIBOLD}>
          {title}
        </BodyTyp>
        <StyledRightComponent>
          {rightComponent}
          <IconManager size={IconSizeEnum.SMALL} onClick={onClose}>
            <CloseIcon />
          </IconManager>
        </StyledRightComponent>
      </StyledPanelHeader>
      {children}
    </StyledPanel>
  );
};
